import { FC } from 'react';
import {
  Link,
  ListTotal,
  TableToolbar,
  TableWrapper,
  Table,
  Typography,
  Button,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useRouteParams } from 'utils/useIdParam';
import { APP, ROUTES } from 'config';
import { useModalContext } from 'store/modal';
import { useSearchParams } from 'utils/navigation';
import {
  COLOR_BY_STATUS,
  assignmentStatuses,
  employeeGroupsByEmployeeFiltersSchema,
  useAssignGroupsToEmployee,
  useEmployeeGroupsByEmployeeAssignments,
} from 'store/assignments';
import {
  EmployeesGroupsFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import {
  AssignmentDateSelectModal,
  EmployeeEmployeeGroupAssignmentHistory,
} from 'containers/assignments';
import qs from 'qs';
import { useMe } from 'state/Administrators';

export const EmployeeGroupsAssignments: FC = () => {
  const { t } = useTranslation();
  const { id: employeeId } = useRouteParams(ROUTES.employees.details);
  const { openModal } = useModalContext();
  const { canEditGroups, hasEditPermission } = useMe();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { searchParams: filters, navigate } = useSearchParams(
    employeeGroupsByEmployeeFiltersSchema,
  );

  const { data: assignments, isLoading } =
    useEmployeeGroupsByEmployeeAssignments(employeeId, filters);

  const { mutate: assignGroupsToEmployee, isLoading: isAssigning } =
    useAssignGroupsToEmployee();

  return (
    <TableWrapper>
      <TableToolbar sticky>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap="10px"
        >
          <ListTotal total={assignments?.total} />

          <Stack direction="row" alignItems="center" gap="10px">
            <Filters
              value={filters}
              initialValues={employeeGroupsByEmployeeFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              onChangeCallback={() => setSelectedRowIds({})}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('entity_type.employeeGroup')}
                    value={value.employeeGroups}
                    initialValue={initialValues.employeeGroups}
                    onChange={(v) => setFilter('employeeGroups', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <EmployeesGroupsFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('alternatives.assignmentStatus')}
                    value={value.assignmentStatus}
                    initialValue={initialValues.assignmentStatus}
                    onChange={(v) => setFilter('assignmentStatus', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        options={assignmentStatuses.map((status) => ({
                          key: status,
                          label: t(`status.${status}`),
                        }))}
                        searchable={false}
                        multiple={true}
                      />
                    )}
                  </FilterButton>
                </>
              )}
            </Filters>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={assignments?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('groups.group_name'),
            cell: ({ row: { original: assignment } }) => {
              return (
                <Link
                  label={assignment.name}
                  to={{
                    pathname: ROUTES.employeeGroups.details.replace(
                      ':id',
                      assignment.id,
                    ),
                    search: qs.stringify({ tab: 'innerGroupAssignEmployees' }),
                  }}
                />
              );
            },
          },
          {
            id: 'assignmentStatus',
            header: t('alternatives.assignmentStatus'),
            meta: { align: 'center' },
            cell: ({ row: { original: assignment } }) => {
              return (
                <Stack
                  flexDirection="row"
                  gap={1.5}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography
                    color={COLOR_BY_STATUS[assignment.assignmentStatus]}
                  >
                    {t(`status.${assignment.assignmentStatus}`)}
                  </Typography>

                  {assignment.assignmentCount ? (
                    <Button
                      variant="neutral-outline"
                      size="sm"
                      icon={AccessTime}
                      onClick={() => {
                        openModal({
                          component: EmployeeEmployeeGroupAssignmentHistory,
                          props: {
                            employeeId,
                            employeeGroupId: assignment.id,
                            readonly:
                              !canEditGroups || !hasEditPermission(['groups']),
                          },
                          size: 'lg',
                        });
                      }}
                      tooltip={t('permissions.historyModule')}
                    />
                  ) : null}
                </Stack>
              );
            },
          },
        ]}
        pagination={
          assignments && {
            count: assignments.total,
            page: assignments.page,
            pageSize: assignments.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        rows={{
          inactive: ({ original: assignment }) =>
            assignment.assignmentStatus === 'off',
        }}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          actions: assignmentStatuses.map((status) => ({
            label: t(`status.${status}`),
            disabled: !canEditGroups || !hasEditPermission(['groups']),
            onClick: async () => {
              const response = await openModal({
                component: AssignmentDateSelectModal,
                props: { withTime: true },
              });

              if (response.action === 'CONFIRM') {
                assignGroupsToEmployee(
                  {
                    employeeId,
                    ...(status === 'on'
                      ? {
                          action: 'create',
                          data: {
                            employeeGroupIds: Object.keys(selectedRowIds),
                            ...(response.isNow
                              ? { fromNow: true }
                              : { from: response.date }),
                          },
                        }
                      : {
                          action: 'update',
                          data: {
                            employeeGroupIds: Object.keys(selectedRowIds),
                            ...(response.isNow
                              ? { toNow: true }
                              : { to: response.date }),
                          },
                        }),
                  },
                  {
                    onSuccess: () => {
                      setSelectedRowIds({});
                    },
                  },
                );
              }
            },
          })),
          isLoading: isAssigning,
        }}
      />
    </TableWrapper>
  );
};
